import { useMemo } from 'react';
import { format } from 'date-fns';
import { type AvailabilityDate } from '@zg-rentals/touring';
import { Box, Text } from '@zillow/constellation-10';

const formatTime = (date: Date) => format(date, 'h:mma').toLowerCase();

const formatAvailabilityMapKey = (date: Date) => format(date, 'eee, MMM d');

const formatAvailabilityDuration = (start: Date, end: Date) => {
  const startTime = formatTime(start);
  const endTime = formatTime(end);
  return `${startTime}-${endTime}`;
};

const TouringAvailability = ({ availabilityDates }: { availabilityDates: Array<AvailabilityDate> }) => {
  const formattedAvailabilityDates: Array<[string, Array<string>]> = useMemo(
    () =>
      availabilityDates.map((availabilityDate) => {
        const timeSlots = availabilityDate.timeSlots.map((timeSlot) =>
          formatAvailabilityDuration(timeSlot.start, timeSlot.end),
        );
        return [formatAvailabilityMapKey(new Date(availabilityDate.date)), timeSlots];
      }),
    [availabilityDates],
  );

  if (!availabilityDates.length) {
    return <Text color="text.subtle">Action needed</Text>;
  }

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: { base: 'sm', md: 'md' } }}>
      {formattedAvailabilityDates.map(([date, timeSlots]) => (
        <Box key={date} css={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <Text>{date}</Text>
          <Box css={{ display: 'flex', flexDirection: 'column' }}>
            {timeSlots.map((timeSlot) => (
              <Text key={timeSlot}>{timeSlot}</Text>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default TouringAvailability;
